import { useState, createContext, useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios from '../utils/axios';
import localStorageAvailable from '../utils/localStorageAvailable';
import { setStorage, StorageType } from '../utils/storage';
//
import { setSession } from './utils';
import { ActionMapType, AuthStateType, AuthUserType, JWTContextType } from './types';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

export enum Types {
  RESET = 'RESET',
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
}

type Payload = {
  [Types.RESET]: undefined;
  [Types.INITIAL]: {
    isAuthenticated: boolean;
    isSubscriber?: boolean;
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
    isSubscriber?: boolean;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  isSubscriber: false,
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.RESET) {
    return initialState;
  }
  if (action.type === Types.INITIAL) {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      isSubscriber: action.payload.isSubscriber,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      isAuthenticated: true,
      isSubscriber: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      isAuthenticated: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isSocialLogining, setIsSocialLogining] = useState(false);

  const storageAvailable = localStorageAvailable();

  // UPDATE USER INFO
  const updateUserInfo = useCallback(async (isNeedResetState: boolean = false) => {

    // Reset State 
    if(isNeedResetState) {
      dispatch({
        type: Types.RESET
      });
    }
    
    try {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      if (accessToken) {
        setSession(accessToken);

        const response = await axios.get('/account/user/');
        const user = response.data;
        setStorage(StorageType.userId, user?.pk)
        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: true,
            isSubscriber: true, // If false, it will redirect to pricing page.
            user: {
              displayName: `${user.first_name} ${user.last_name}`,
              photoURL: '',
              ...user,
            },
          },
        });
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error?.data || error);
      if (error?.status === 401) {
        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
      if (error?.status === 403) {
        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: true,
            isSubscriber: false,
            user: null,
          },
        });
      }
    }
  }, [storageAvailable]);

  // INITIALIZE
  const initialize = useCallback(async () => {
    await updateUserInfo(true);
  }, [updateUserInfo]);

  useEffect(() => {
    initialize();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // LOGIN
  const login = useCallback(async (email: string, password: string) => {
    setSession(null);
    const response = await axios.post('/account/login/', {
      email,
      password,
    });
    const { key } = response.data;
    
    localStorage.setItem('accessToken', key);
    setSession(key);
    initialize(); // get user profile

  }, [initialize]);

  const loginWithGoogle = useCallback(async (token: string) => {
    setSession(null);
    setIsSocialLogining(true);
    const response = await axios.post('/account/google/', {
      access_token: token,
      id_token: token,
    });

    const { key } = response.data;
    localStorage.setItem('accessToken', key);
    setSession(key);
    await initialize(); // get user profile

  }, [initialize]);

  const loginWithGithub = useCallback(async (token: string) => {
    setSession(null);
    setIsSocialLogining(true);
    const response = await axios.post('/account/github/', {
      access_token: token,
      id_token: token,
    });

    const { key } = response.data;
    localStorage.setItem('accessToken', key);
    setSession(key);
    await initialize(); // get user profile

  }, [initialize]);

  // REGISTER
  const register = useCallback(
    async (email: string, password: string, firstName: string, lastName: string, passwordConfirm: string,) => {
      setSession(null);
      await axios.post('/account/registration/', {
        email,
        username: email,
        first_name: firstName,
        last_name: lastName,
        password1: password,
        password2: passwordConfirm,
      });
      dispatch({
        type: Types.REGISTER,
        payload: {
          user: {
            displayName: `${firstName} ${lastName}`,
            email,
            photoURL: '',
          },
        },
      });
    },
    []
  );

  // LOGOUT
  const logout = useCallback(async () => {
    dispatch({
      type: Types.LOGOUT,
    });
    try {
      await axios.post('/account/logout/');
    } finally {
      setSession(null);
    }
    
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isSocialLogining,
      setIsSocialLogining,
      isInitialized: state.isInitialized,
      isSubscriber: state.isSubscriber,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: 'jwt',
      login,
      loginWithGoogle,
      loginWithGithub,
      loginWithTwitter: () => {},
      updateUserInfo,
      register,
      logout,
    }),
    [updateUserInfo, isSocialLogining, state.isSubscriber, state.isAuthenticated, state.isInitialized, state.user, login, loginWithGoogle, loginWithGithub, logout, register]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
